import {Component, ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

import {UserService} from 'app/profile/services/user.service';
import {getTranslation} from 'shared/helpers/get-translation';
import {NavService} from 'shared/nav/nav.service';
import {SearchResponse, SearchResponseCont, LocalisedText, SearchResponseQuest, SearchResponseTiplist} from '../../model/model';
import {SearchService} from '../../services/search.service';
import { DefaultPage } from 'app/menu/components/default-page/default-page';
import { UserProfile } from 'app/profile/model/model';
import { TipService } from 'app/tips';
import { IonInput } from '@ionic/angular';

@Component({
  selector: 'search-page',
  templateUrl: 'search-page.html',
  styleUrls: ['search-page.scss'],
})
export class SearchPage extends DefaultPage {

  @ViewChild('searchbar') searchbarInput!: IonInput;
  
  public searchValue: string = '';
  public query: string = '';
  public doFocus = false;

  public no_keywords: string = '(ohne Keywords)';

  public result: SearchResponse =  {
    content: [],
    questionChain: [],
    tiplist: [],
  };

  public resCont: Dictionary<Array<SearchResponseCont>> = {};
  public hasContent: boolean = false;

  public resQuest: Dictionary<Array<SearchResponseCont>> = {};
  public hasQuestionChain: boolean = false;

  public resTiplist: Dictionary<Array<SearchResponseTiplist>> = {};
  public hasTiplist: boolean = false;

  public collapsMarker: Dictionary<boolean> = {}; 

  constructor(
    private navService: NavService,
    private translateService: TranslateService,
    private searchService: SearchService,
    private userService: UserService,
    private tipService: TipService
  ) {
    super(navService);
    this.translateService
      .get('SEARCH.NO_KEYWORDS')
      .subscribe((text) => { this.no_keywords = text; });
    if ( navService.get('search') !== undefined ) {
      this.searchValue = navService.get('search');
      this.query = this.searchValue.toLowerCase();
      this.runSearch();
    }
    if ( navService.get('focus') !== undefined && navService.get('focus') ) {
      this.doFocus = true;
    }
  }

  ionViewDidEnter() {
    if (this.doFocus) {
      this.searchbarInput.setFocus();
    }
  }

  handleInput(event) {
    this.query = event.target.value.toLowerCase();
    this.runSearch();
  }

  private runSearch(): void {
    this.searchService.getSearchResults({
      text: this.query,
      content: true,
      questionChain: true,
      maxResult: 20
    })
      .subscribe((res) => {
        this.result = res;
        this.prepareSearchResponseCont(this.query, res.content);
        this.prepareSearchResponseQuest(this.query, res.questionChain);
        this.prepareSearchResponseTiplist(this.query, res.tiplist);
      });
  }

  prepareSearchResponseCont(query:string, res: Array<SearchResponseCont>): void {
    const _resCont: Array<Dictionary<Array<SearchResponseCont>>> = [{}, {}]; // Treffer in Keyword / kein Treffer in Keyword
    res.forEach(resp => {
      let keyword = '';
      let keywordMatches = 1;
      if (resp.keywords) {
        const keywordRes = this.getKeyword(query, resp.keywords);
        keyword = keywordRes[0];
        keywordMatches = keywordRes[1] ? 0 : 1;
      }
      if (_resCont[keywordMatches][keyword] === undefined) {
        _resCont[keywordMatches][keyword] = [];
      }
      _resCont[keywordMatches][keyword].push(resp);
    });
    // sortieren und zusammensetzen
    this.resCont = {...this.sortDict(_resCont[0]), ...this.sortDict(_resCont[1])};
    this.hasContent = (Object.keys(this.resCont).length > 0);
    if (Object.keys(_resCont[0]).length > 0) {
      this.collapsMarker['cont'] = true;
    }
  }

  prepareSearchResponseQuest(query:string, res: Array<SearchResponseQuest>): void {
    const _resQuest: Array<Dictionary<Array<SearchResponseQuest>>> = [{}, {}]; // Treffer in Keyword / kein Treffer in Keyword
    res.forEach(resp => {
      let keyword = '';
      let keywordMatches = 1;
      if (resp.keywords) {
        const keywordRes = this.getKeyword(query, resp.keywords);
        keyword = keywordRes[0];
        keywordMatches = keywordRes[1] ? 0 : 1;
      }
      if (_resQuest[keywordMatches][keyword] === undefined) {
        _resQuest[keywordMatches][keyword] = [];
      }
      _resQuest[keywordMatches][keyword].push(resp);
    });
    // sortieren und zusammensetzen
    this.resQuest = {...this.sortDict(_resQuest[0]), ...this.sortDict(_resQuest[1])};
    this.hasQuestionChain = (Object.keys(this.resQuest).length > 0);
    if (Object.keys(_resQuest[0]).length > 0) {
      this.collapsMarker['quest'] = true;
    }
  }

  prepareSearchResponseTiplist(query:string, res: Array<SearchResponseTiplist>): void {
    const _resTiplist: Array<Dictionary<Array<SearchResponseTiplist>>> = [{}, {}]; // Treffer in Keyword / kein Treffer in Keyword
    res.forEach(resp => {
      let keyword = '';
      let keywordMatches = 1;
      if (resp.tiplist.keywords) {
        const keywordRes = this.getKeyword(query, resp.tiplist.keywords);
        keyword = keywordRes[0];
        keywordMatches = keywordRes[1] ? 0 : 1;
      }
      if (_resTiplist[keywordMatches][keyword] === undefined) {
        _resTiplist[keywordMatches][keyword] = [];
      }
      // evtl. tiplist ausdünnen wenn kein Keyword match
      if (keywordMatches == 1) {
        resp.tiplist.tips = resp.tiplist.tips.filter(tip => {
          return this.matchQuery(tip.title, query) 
            || this.matchQuery(tip.text.de, query)
            || this.matchQuery(tip.text.en, query);
        });
      }
      if (resp.tiplist.tips.length > 0) {
        _resTiplist[keywordMatches][keyword].push(resp);
      }
    });
    // sortieren und zusammensetzen
    this.resTiplist = {...this.sortDict(_resTiplist[0]), ...this.sortDict(_resTiplist[1])};
    this.hasTiplist = (Object.keys(this.resTiplist).length > 0);
    if (Object.keys(_resTiplist[0]).length > 0) {
      this.collapsMarker['tiplist'] = true;
    }
  }

  matchQuery(text: string, query: string): boolean {
    if (text === undefined) {
      return false;
    }
    return text.toLowerCase().indexOf(query) != -1;
  }

  getKeyword(query: string, keywords: LocalisedText): any {
    let keyword = '';
    const s = getTranslation(keywords, this.translateService);
    const b = s.split(';');
    b.forEach(a => {
      if (this.matchQuery(a.trim(), query)) {
        if (keyword == '') {
          keyword = a.trim();
        }
      }
    });
    if (keyword != '') {
      return [keyword, true];
    }
    if ( keywords.de !== undefined) {
      const b = keywords.de.split(';');
      b.forEach(a => {
        if (this.matchQuery(a.trim(), query)) {
          if (keyword == '') {
            keyword = a.trim();
          }
        }
      });
    }
    if (keyword != '') {
      return [keyword, true];
    }
    if (keywords.en !== undefined) {
      const b = keywords.en.split(';');
      b.forEach(a => {
        if (this.matchQuery(a.trim(), query)) {
          if (keyword == '') {
            keyword = a.trim();
          }
        }
      });
    }
    if (keyword != '') {
      return [keyword, true];
    }
    return [b[0].trim(), false];
  }

  sortDict(dict: Dictionary<any>): Dictionary<any> {
    // Sort the keys
    const sortedKeys = Object.keys(dict).sort();
 
    // Create a new object with sorted keys
    return sortedKeys.reduce((acc, key) => {
        acc[key] = dict[key];
        return acc;
    }, {});
  }

  public trackByResCont(index: number, resCont: any): string {
    return resCont.key;
  }

  public trackByCont(index: number, cont: SearchResponseCont): string {
    return cont._id;
  }

  public showCont(id: string): void {
    const contId = id.substring(6);
    console.log("show cont "+contId);
    this.userService.getUserProfile('contents')
      .subscribe((user: UserProfile) => {
        if (user.contents !== undefined) {
          const userContent = user.contents.find(cont => cont.content == contId);
          if (userContent !== undefined) {
            if (userContent.state >= 5) {
              this.navService.push('content/flow', { userContent: userContent });
            }
            else { // available or suggested
              this.navService.push('content', {selUserContent: userContent});
            }
          }
        }
      });
  }

  public trackByResQuest(index: number, resCont: any): string {
    return resCont.key;
  }

  public trackByQuest(index: number, cont: SearchResponseCont): string {
    return cont._id;
  }

  public showQuest(id: string): void {
    const questId = id.substring(7);
    console.log("show quest "+questId);
    this.tipService.getQuestion(questId).subscribe((question) => {
      this.navService.push('tips/' + question._id, {
        question: question,
       // overrideTitle: title,
        isInReflectionMode: false,
      });
    });
    /*this.userService.getUserProfile('contents')
      .subscribe((user: UserProfile) => {
        if (user.contents !== undefined) {
          const userContent = user.contents.find(cont => cont.content == contId);
          if (userContent !== undefined) {
            if (userContent.state >= 5) {
              this.navService.push('content/flow', { userContent: userContent });
            }
            else { // available or suggested
              this.navService.push('content', {selUserContent: userContent});
            }
          }
        }
      });*/
  }

  public trackByResTiplist(index: number, resTiplist: any): string {
    return resTiplist.key;
  }

  public trackByTiplist(index: number, tiplist: SearchResponseTiplist): string {
    return tiplist._id;
  }

  public showTiplist(tip_id: string, tiplist: SearchResponseTiplist): void {
    console.log("show tiplist "+tip_id);
    console.log("show tiplist %o", tiplist);

    var tips = tiplist.tiplist.tips.sort((a, b) => {
      if (a._id == tip_id) {
        return -1;
      }
      if (b._id == tip_id) {
        return 1;
      }
      return 0;
    });
    this.navService.push('tips/' + (new Date()).getTime(), {
      tipList: tips,
      //questionTime: this.questionTime,
      //currentQuestionTime: this.currentQuestionTime,
      isInReflectionMode: false,
      subtree: 'tipStart',
    });
  }

}
